import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { toast } from "react-toastify";
import input from "../../../../assets/styles/components/input.module.css";
import {
  getAccount,
  patchAccount,
  postAccount,
} from "../../../../services/financeAccount";
import type { value } from "../../../../types/financeAccount";
import styles from "./Modal.module.css";
import Button from "../../../../components/Button/Button";
import ModalGeneric from "../../../../components/ModalGeneric/ModalGeneric";

type prop = {
  onclose: () => void;
  title: string;
  uid?: string;
};

const Modal = ({ onclose, title, uid }: prop) => {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ["getAccount", uid],
    queryFn: () => getAccount(uid || ""),
    enabled: !!uid,
  });

  const resetInput = () =>
    setValue({
      ativo: 1,
      nome: "",
      tipo: "caixa",
      saldo_inicial: 0,
      data_saldo_inicial: "",
    });

  const [value, setValue] = useState<value>({
    ativo: 1,
    nome: "",
    tipo: "caixa",
    saldo_inicial: 0,
    data_saldo_inicial: "",
  });

  const { mutate } = useMutation({
    mutationFn: async (data: value) => {
      if (uid) {
        const res = await patchAccount({
          uid_conta_financeira: uid,
          ...data,
        });
        return res;
      } else {
        const res = await postAccount(data);
        return res;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["getFinanceAccount"] });
      if (!uid) {
        resetInput();
      }
      toast.success(data.message);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  useEffect(() => {
    if (!data || Object.keys(data).length === 0) {
      resetInput();
      return;
    } else {
      const { ativo, nome, tipo, saldo_inicial, data_saldo_inicial } = data;
      setValue({
        ativo: ativo ?? 1,
        nome: nome ?? "",
        tipo: tipo ?? "caixa",
        saldo_inicial: saldo_inicial ?? 0, 
        data_saldo_inicial: data_saldo_inicial ?? "",
      });
    }
  }, [data]);

  const handleInput = (e: string | number, valueName: string) => {
    if (valueName === "nome" && typeof e === "string" && e.length > 100) return;

    setValue((prev) => ({ ...prev, [valueName]: e }));
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();

    const differentValues = Object.keys(value || {})
      .filter(
        (k) =>
          data?.[k as keyof typeof data] !== value[k as keyof typeof value],
      )
      .reduce(
        (acc, k) => ({ ...acc, [k]: value?.[k as keyof typeof value] }),
        {} as Partial<value>,
      );

    if (Object.values(differentValues).length === 0) return;

    mutate(differentValues as value);
  };

  const formatCurrencyDisplay = (val: number | string | null) => {
    const numberValue = Number(val) || 0;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numberValue);
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, "");
    const floatValue = Number(rawValue) / 100;

    handleInput(floatValue, "saldo_inicial");
  };

  return (
    <ModalGeneric
      title={title}
      onClick={onclose}
      contentClass={styles.contentFinance}
      modalGenericCustom={styles.widthFinance}
    >
      <form className={styles.containerFinance} onSubmit={submit}>
        <label>
          Nome
          <input
            className={input.input}
            value={value.nome}
            onChange={(e) => handleInput(e.target.value, "nome")}
            placeholder="Informe o nome da conta ex. Caixa, Banco Bradesco, Itaú, PIX... "
          />
        </label>
        <label>
          Tipo
          <select
            className={input.input}
            value={value.tipo}
            onChange={(e) => handleInput(e.target.value, "tipo")}
          >
            <option value="caixa">Caixa</option>
            <option value="banco">Banco</option>
            <option value="pix">Pix</option>
            <option value="carteira">Carteira</option>
            <option value="outro">Outro</option>
          </select>
        </label>
        <label>
          Status
          <select
            className={input.input}
            value={value.ativo}
            onChange={(e) => handleInput(e.target.value, "ativo")}
          >
            <option value="1">Ativo</option>
            <option value="0">Inativo</option>
          </select>
        </label>
        <label>
          Saldo inicial
          <input
            className={input.input}
            type="text"
            inputMode="numeric"
            placeholder="0,00"
            value={formatCurrencyDisplay(value.saldo_inicial)} 
            onChange={handleCurrencyChange}
            maxLength={15}
          />
        </label>
        <label>
          Data do saldo inicial
          <input
            type="date"
            className={input.input}
            value={value.data_saldo_inicial}
            onChange={(e) => handleInput(e.target.value, "data_saldo_inicial")}
            placeholder="Data do saldo inicial"
          />
        </label>
        <div className={styles.save}>
          <Button type="submit" variant="contain">
            Salvar
          </Button>
        </div>
      </form>
    </ModalGeneric>
  );
};

export default Modal;
